import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { 
  AlertTriangle, Clock, MapPin, Activity, ShieldCheck, CheckCircle, 
  Search, Filter, Bell, Calendar, Home, Settings, FileText, 
  ChevronDown, ChevronRight, X, ArrowRight, Users, MessageSquare, 
  Phone, Check, Info, Eye, RefreshCw
} from 'lucide-react';
import { generateSafetyData } from './safety-alerts/safetyDataGenerator';
import { SafetyAlert, SafetyStats, RiskZone, SafetyRecommendation, AlertHistoryItem, NotificationSettings } from './safety-alerts/types';
import DashboardTab from './safety-alerts/DashboardTab';
import ActiveAlertsTab from './safety-alerts/ActiveAlertsTab';
import RiskAnalysisTab from './safety-alerts/RiskAnalysisTab';
import RecommendationsTab from './safety-alerts/RecommendationsTab';
import SettingsTab from './safety-alerts/SettingsTab';

type ActiveTab = 'dashboard' | 'active' | 'risk' | 'recommendations' | 'settings';

const SafetyAlerts: React.FC = () => {
  // Main data state
  const [alerts, setAlerts] = useState<SafetyAlert[]>([]);
  const [stats, setStats] = useState<SafetyStats | null>(null);
  const [riskZones, setRiskZones] = useState<RiskZone[]>([]);
  const [recommendations, setRecommendations] = useState<SafetyRecommendation[]>([]);
  const [alertHistory, setAlertHistory] = useState<AlertHistoryItem[]>([]);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings | null>(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState<ActiveTab>('dashboard');
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [filterBy, setFilterBy] = useState<{
    severity: ('HIGH' | 'MEDIUM' | 'LOW')[];
    location: string[];
    status: ('ACTIVE' | 'ACKNOWLEDGED' | 'RESOLVED')[];
    type: ('FALL_RISK' | 'WANDERING' | 'ENVIRONMENTAL' | 'BEHAVIORAL')[];
  }>({
    severity: [],
    location: [],
    status: [],
    type: []
  });
  const [sortBy, setSortBy] = useState<'timestamp' | 'severity' | 'location'>('timestamp');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [liveMode, setLiveMode] = useState<boolean>(false);
  
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const data = generateSafetyData();
      setAlerts(data.alerts);
      setStats(data.stats);
      setRiskZones(data.riskZones);
      setRecommendations(data.recommendations);
      setAlertHistory(data.alertHistory);
      setNotificationSettings(data.notificationSettings);
      
      setLoading(false);
    };
    
    loadData();
  }, []);
  
  // Simulate live data updates when in live mode
  useEffect(() => {
    if (!liveMode) return;
    
    const interval = setInterval(() => {
      // Simulate new alerts occasionally
      const random = Math.random();
      if (random < 0.2) { // 20% chance of new alert
        const data = generateSafetyData(1);
        setAlerts(prev => [...data.alerts, ...prev]);
        // Update stats
        setStats(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            activeAlerts: prev.activeAlerts + 1,
            totalAlerts: prev.totalAlerts + 1
          };
        });
      }
      
      // Update existing alerts (acknowledge or resolve some)
      if (random > 0.7) { // 30% chance
        setAlerts(prev => 
          prev.map((alert, index) => {
            if (index === 0 && alert.status === 'ACTIVE') {
              return {
                ...alert,
                status: 'ACKNOWLEDGED',
                response: {
                  time: new Date(),
                  action: 'Auto-acknowledged by system',
                  responder: 'System'
                }
              };
            }
            return alert;
          })
        );
      }
    }, 5000); // Check every 5 seconds
    
    return () => clearInterval(interval);
  }, [liveMode]);
  
  // Handle alert acknowledgment
  const handleAcknowledgeAlert = (alertId: string) => {
    setAlerts(prev => 
      prev.map(alert => 
        alert.id === alertId 
          ? {
              ...alert,
              status: 'ACKNOWLEDGED',
              response: {
                time: new Date(),
                action: 'Acknowledged by caregiver',
                responder: 'Caregiver'
              }
            }
          : alert
      )
    );
    
    // Update stats
    setStats(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        activeAlerts: prev.activeAlerts - 1,
        acknowledgedAlerts: prev.acknowledgedAlerts + 1
      };
    });
  };
  
  // Handle alert resolution
  const handleResolveAlert = (alertId: string, notes?: string) => {
    setAlerts(prev => 
      prev.map(alert => 
        alert.id === alertId 
          ? {
              ...alert,
              status: 'RESOLVED',
              response: {
                time: new Date(),
                action: 'Resolved by caregiver' + (notes ? `: ${notes}` : ''),
                responder: 'Caregiver'
              }
            }
          : alert
      )
    );
    
    // Update stats
    setStats(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        acknowledgedAlerts: prev.acknowledgedAlerts - 1,
        resolvedAlerts: prev.resolvedAlerts + 1
      };
    });
    
    // Add to history
    const resolvedAlert = alerts.find(alert => alert.id === alertId);
    if (resolvedAlert) {
      setAlertHistory(prev => [
        {
          id: `hist-${Date.now()}`,
          alertId: resolvedAlert.id,
          alertType: resolvedAlert.type,
          severity: resolvedAlert.severity,
          location: resolvedAlert.location.room,
          createdAt: resolvedAlert.timestamp,
          resolvedAt: new Date(),
          resolution: notes || 'Resolved by caregiver',
          timeToResolve: Math.floor((Date.now() - resolvedAlert.timestamp.getTime()) / 60000) // minutes
        },
        ...prev
      ]);
    }
  };
  
  // Handle adding a note to caregiver portal
  const handleSendToCaregiverPortal = (alertId: string, note: string) => {
    // In a real app, this would integrate with the caregiver portal
    console.log(`Alert ${alertId} sent to caregiver portal with note: ${note}`);
    
    // For demo purposes, update the alert with a note
    setAlerts(prev => 
      prev.map(alert => 
        alert.id === alertId 
          ? {
              ...alert,
              caregiverNotes: [...(alert.caregiverNotes || []), {
                timestamp: new Date(),
                content: note,
                author: 'Current User'
              }]
            }
          : alert
      )
    );
  };
  
  // Handle implementing a recommendation
  const handleImplementRecommendation = (recommendationId: string) => {
    setRecommendations(prev => 
      prev.map(rec => 
        rec.id === recommendationId 
          ? { ...rec, status: 'IMPLEMENTED', implementedAt: new Date() }
          : rec
      )
    );
  };
  
  // Handle saving notification settings
  const handleSaveSettings = (settings: NotificationSettings) => {
    setNotificationSettings(settings);
  };
  
  // Filter alerts based on search and filters
  const filteredAlerts = alerts.filter(alert => {
    // Search filter
    if (searchTerm && 
        !alert.description.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !alert.location.room.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    // Severity filter
    if (filterBy.severity.length > 0 && !filterBy.severity.includes(alert.severity)) {
      return false;
    }
    
    // Location filter
    if (filterBy.location.length > 0 && !filterBy.location.includes(alert.location.room)) {
      return false;
    }
    
    // Status filter
    if (filterBy.status.length > 0 && !filterBy.status.includes(alert.status)) {
      return false;
    }
    
    // Type filter
    if (filterBy.type.length > 0 && !filterBy.type.includes(alert.type)) {
      return false;
    }
    
    return true;
  });
  
  // Sort filtered alerts
  const sortedAlerts = [...filteredAlerts].sort((a, b) => {
    if (sortBy === 'timestamp') {
      return sortDirection === 'desc' 
        ? b.timestamp.getTime() - a.timestamp.getTime()
        : a.timestamp.getTime() - b.timestamp.getTime();
    }
    
    if (sortBy === 'severity') {
      const severityOrder = { HIGH: 3, MEDIUM: 2, LOW: 1 };
      return sortDirection === 'desc'
        ? severityOrder[b.severity] - severityOrder[a.severity]
        : severityOrder[a.severity] - severityOrder[b.severity];
    }
    
    if (sortBy === 'location') {
      return sortDirection === 'desc'
        ? b.location.room.localeCompare(a.location.room)
        : a.location.room.localeCompare(b.location.room);
    }
    
    return 0;
  });
  
  // Toggle filters
  const toggleSeverityFilter = (severity: 'HIGH' | 'MEDIUM' | 'LOW') => {
    setFilterBy(prev => {
      const severities = [...prev.severity];
      if (severities.includes(severity)) {
        return { ...prev, severity: severities.filter(s => s !== severity) };
      } else {
        return { ...prev, severity: [...severities, severity] };
      }
    });
  };
  
  const toggleLocationFilter = (location: string) => {
    setFilterBy(prev => {
      const locations = [...prev.location];
      if (locations.includes(location)) {
        return { ...prev, location: locations.filter(l => l !== location) };
      } else {
        return { ...prev, location: [...locations, location] };
      }
    });
  };
  
  const toggleStatusFilter = (status: 'ACTIVE' | 'ACKNOWLEDGED' | 'RESOLVED') => {
    setFilterBy(prev => {
      const statuses = [...prev.status];
      if (statuses.includes(status)) {
        return { ...prev, status: statuses.filter(s => s !== status) };
      } else {
        return { ...prev, status: [...statuses, status] };
      }
    });
  };
  
  const toggleTypeFilter = (type: 'FALL_RISK' | 'WANDERING' | 'ENVIRONMENTAL' | 'BEHAVIORAL') => {
    setFilterBy(prev => {
      const types = [...prev.type];
      if (types.includes(type)) {
        return { ...prev, type: types.filter(t => t !== type) };
      } else {
        return { ...prev, type: [...types, type] };
      }
    });
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilterBy({
      severity: [],
      location: [],
      status: [],
      type: []
    });
    setSearchTerm('');
  };
  
  // Toggle sort direction
  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };
  
  // Set sort field
  const handleSetSortBy = (field: 'timestamp' | 'severity' | 'location') => {
    if (sortBy === field) {
      toggleSortDirection();
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };
  
  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-3 text-gray-600">Loading safety alerts data...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold">Safety Alerts</h2>
          <p className="text-gray-600">
            Comprehensive safety monitoring and prevention for your loved one
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          <div className="flex items-center">
            <span className={`mr-2 px-3 py-1 rounded-full text-xs font-medium ${liveMode ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
              {liveMode ? 'LIVE' : 'STATIC'}
            </span>
            <button
              onClick={() => setLiveMode(!liveMode)}
              className={`px-3 py-1 rounded-lg text-sm font-medium ${
                liveMode ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
              }`}
            >
              {liveMode ? 'Disable Live Updates' : 'Enable Live Updates'}
            </button>
          </div>
          <button
            onClick={() => {
              setLoading(true);
              setTimeout(() => {
                const data = generateSafetyData();
                setAlerts(data.alerts);
                setStats(data.stats);
                setRiskZones(data.riskZones);
                setRecommendations(data.recommendations);
                setAlertHistory(data.alertHistory);
                setLoading(false);
              }, 500);
            }}
            className="flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <RefreshCw size={14} className="mr-1" />
            Refresh Data
          </button>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Active Alerts</h3>
            <AlertTriangle className="text-red-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{stats?.activeAlerts || 0}</p>
          <p className="text-xs text-gray-500">Requiring attention</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Acknowledged</h3>
            <Eye className="text-yellow-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{stats?.acknowledgedAlerts || 0}</p>
          <p className="text-xs text-gray-500">In progress</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Resolved Today</h3>
            <CheckCircle className="text-green-500" size={20} />
          </div>
          <p className="text-2xl font-bold mt-2">{stats?.resolvedToday || 0}</p>
          <p className="text-xs text-gray-500">Last 24 hours</p>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Risk Level</h3>
            <Activity className="text-blue-500" size={20} />
          </div>
          <p className={`text-2xl font-bold mt-2 ${
            stats?.currentRiskLevel === 'HIGH' ? 'text-red-500' :
            stats?.currentRiskLevel === 'MEDIUM' ? 'text-yellow-500' :
            'text-green-500'
          }`}>
            {stats?.currentRiskLevel || 'LOW'}
          </p>
          <p className="text-xs text-gray-500">Overall assessment</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-6 overflow-x-auto">
        <button
          onClick={() => setActiveTab('dashboard')}
          className={`px-4 py-2 font-medium whitespace-nowrap ${
            activeTab === 'dashboard'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Home className="inline-block mr-2 w-5 h-5" />
          Dashboard
        </button>
        <button
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2 font-medium whitespace-nowrap ${
            activeTab === 'active'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <AlertTriangle className="inline-block mr-2 w-5 h-5" />
          Active Alerts
          {stats && stats.activeAlerts > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
              {stats.activeAlerts}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('risk')}
          className={`px-4 py-2 font-medium whitespace-nowrap ${
            activeTab === 'risk'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Activity className="inline-block mr-2 w-5 h-5" />
          Risk Analysis
        </button>
        <button
          onClick={() => setActiveTab('recommendations')}
          className={`px-4 py-2 font-medium whitespace-nowrap ${
            activeTab === 'recommendations'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <FileText className="inline-block mr-2 w-5 h-5" />
          Recommendations
          {recommendations && recommendations.filter(r => r.priority === 'HIGH' && r.status === 'PENDING').length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-xs bg-yellow-500 text-white rounded-full">
              {recommendations.filter(r => r.priority === 'HIGH' && r.status === 'PENDING').length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-4 py-2 font-medium whitespace-nowrap ${
            activeTab === 'settings'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Settings className="inline-block mr-2 w-5 h-5" />
          Settings
        </button>
      </div>

      {/* Content based on active tab */}
      <div>
        {activeTab === 'dashboard' && stats && (
          <DashboardTab 
            alerts={alerts}
            stats={stats}
            riskZones={riskZones}
            recommendations={recommendations.filter(r => r.priority === 'HIGH')}
            alertHistory={alertHistory.slice(0, 5)}
            onAcknowledgeAlert={handleAcknowledgeAlert}
            onViewAlertDetails={(alertId) => setActiveTab('active')}
          />
        )}
        
        {activeTab === 'active' && (
          <ActiveAlertsTab
            alerts={sortedAlerts}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            filterBy={filterBy}
            toggleSeverityFilter={toggleSeverityFilter}
            toggleLocationFilter={toggleLocationFilter}
            toggleStatusFilter={toggleStatusFilter}
            toggleTypeFilter={toggleTypeFilter}
            clearFilters={clearFilters}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSetSortBy={handleSetSortBy}
            onAcknowledgeAlert={handleAcknowledgeAlert}
            onResolveAlert={handleResolveAlert}
            onSendToCaregiverPortal={handleSendToCaregiverPortal}
          />
        )}
        
        {activeTab === 'risk' && (
          <RiskAnalysisTab
            riskZones={riskZones}
            alerts={alerts}
            stats={stats}
            alertHistory={alertHistory}
          />
        )}
        
        {activeTab === 'recommendations' && (
          <RecommendationsTab
            recommendations={recommendations}
            onImplementRecommendation={handleImplementRecommendation}
            riskZones={riskZones}
          />
        )}
        
        {activeTab === 'settings' && notificationSettings && (
          <SettingsTab
            settings={notificationSettings}
            onSaveSettings={handleSaveSettings}
          />
        )}
      </div>
    </div>
  );
};

export default SafetyAlerts; 